import React from 'react';
import RmaStagePill from './RmaStagePill';
import RmaStageTimeline from './RmaStageTimeline';
import { DeviceTypeChip, Callout, FactRow, Mono, Th, EmptyState } from './RmaBits';
import {
  stageOf, isOverdue, daysOverSla, daysWithManufacturer, slaDaysOf,
  warrantyShort, warrantyEnd, RMA_OUTCOME, TONE, ACTIVITY_DOT, awaitingBenchTest,
} from './rmaHelpers';

// Screen 2 — one RMA in full: where it is, what is on it, what the manufacturer said.

const DEV_COLS = '120px 62px minmax(0,1fr) 132px 132px';
// Pre-dispatch the table gains a trailing action column. Once the box has gone the unit is
// committed to the claim, so the column disappears rather than showing a disabled button.
const DEV_COLS_EDITABLE = '120px 62px minmax(0,1fr) 132px 132px 80px';

function RmaDetail({
  shipment, devicesById, st, isReadOnly,
  onBack, onDispatch, onChase, onReceive, onBenchTest, onClose, onRemoveDevice, onEdit,
  onExportCsv, onPrint,
  FilterSelect, DatePicker, darkMode, OW, DM,
  now = Date.now(),
}) {
  const [chaseOpen, setChaseOpen] = React.useState(false);
  const [chaseNote, setChaseNote] = React.useState('');
  // Dispatch is a confirmation step, not a one-click action: it is the moment the parcel
  // leaves the bench, and it is the only chance to record the courier reference that the
  // whole claim is later traced by. An RMA saved with "tracking can be added later" has
  // nowhere else to put that number, which is what this panel fixes.
  const [dispatchOpen, setDispatchOpen] = React.useState(false);
  const [dCarrier, setDCarrier] = React.useState(shipment.carrier || '');
  const [dTracking, setDTracking] = React.useState(shipment.trackingNumber || '');
  const [dDate, setDDate] = React.useState('');
  const [dispatching, setDispatching] = React.useState(false);

  const settings = st.rmaSettings;
  const months = settings?.warrantyMonths;
  const stage = stageOf(shipment);
  const devs = React.useMemo(
    () => (shipment.deviceIds || []).map(id => devicesById.get(id)).filter(Boolean),
    [shipment.deviceIds, devicesById]
  );
  const missing = (shipment.deviceIds || []).length - devs.length;
  // Memoised so the `|| {}` fallback cannot hand a fresh identity to the close-summary
  // memo on every render — the same reason RmaNew memoises its `|| []` fallbacks.
  const outcomes = React.useMemo(() => shipment.outcomes || {}, [shipment.outcomes]);
  const late = isOverdue(shipment, now, settings);
  const awayDays = daysWithManufacturer(shipment, now);
  const sla = slaDaysOf(shipment, settings);
  const pendingBench = awaitingBenchTest(shipment);

  // Units that were out of warranty when the box was sent — judged at dispatch, so this
  // does not change as time passes.
  //
  // Derived by walking `devs` rather than Object.entries(outcomes): device ids are numbers
  // but object keys are always strings, so an entries() key cannot be looked up in a
  // number-keyed device map.
  const chargeable = devs.filter(d => outcomes[d.id]?.chargeable);
  const inWarrantyCount = devs.length - chargeable.length;

  const types = [...new Set(devs.map(d => d.type).filter(Boolean))];
  const faultLabel = shipment.faultMode === 'per_device' ? 'Per-device faults' : (shipment.faultCategory || 'Fault not categorised');

  // Outcome tally used by the close confirmation. Counted off `devs` so it agrees with the
  // table the operator is looking at.
  const closeSummary = React.useMemo(() => {
    const counts = {};
    devs.forEach(d => {
      const key = outcomes[d.id]?.outcome;
      const label = key ? (RMA_OUTCOME[key]?.label || key) : 'no outcome recorded';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([label, n]) => ({ label, n }));
  }, [devs, outcomes]);

  // Only legal before dispatch — removeDeviceFromRma enforces the same rule, this just
  // stops the UI offering something the transition would refuse. A one-unit RMA cannot be
  // emptied: an RMA with no devices is not a record anyone can act on.
  const canRemove = !isReadOnly && stage === 'awaiting_dispatch' && devs.length > 1 && !!onRemoveDevice;
  const devCols = canRemove ? DEV_COLS_EDITABLE : DEV_COLS;

  const canDispatch = !isReadOnly && stage === 'awaiting_dispatch';
  // Reopening the wizard is the full edit: units, fault and shipment fields. Only before
  // dispatch — updateRmaShipment refuses anything later, this keeps the UI honest about it.
  const canEdit = !isReadOnly && stage === 'awaiting_dispatch' && !!onEdit;
  const canReceive = !isReadOnly && stage === 'with_manufacturer';
  const canChase = !isReadOnly && stage === 'with_manufacturer';
  const canClose = !isReadOnly && stage === 'returned_to_bench' && pendingBench.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Header */}
      <div>
        <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          All RMAs
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Mono size={22} weight={600} style={{ letterSpacing: '-.3px' }}>{shipment.ref || shipment.id}</Mono>
              <RmaStagePill shipment={shipment} showOverdue={false} settings={settings} now={now} />
              {awayDays !== null && stage !== 'closed' && (
                <span className={'pill ' + (late ? 'pill-danger' : 'pill-warning')}>
                  Day {awayDays} · SLA {sla} d
                </span>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 6 }}>
              {devs.length} {types.length === 1 ? '× ' + types[0] : types.length > 1 ? 'units, mixed types' : 'units'}
              {' · '}{faultLabel}
              {shipment.createdAt && ' · raised ' + new Date(shipment.createdAt).toLocaleDateString('en-GB')}
              {shipment.createdBy && ' by ' + shipment.createdBy}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            <button onClick={onExportCsv} className="btn btn-ghost">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 11l5 5 5-5M4 21h16" /></svg>
              Export CSV
            </button>
            <button onClick={onPrint} className="btn btn-ghost">Print sheet</button>
            {canChase && (
              <button onClick={() => setChaseOpen(o => !o)} className="btn btn-ghost">Chase manufacturer</button>
            )}
            {canEdit && (
              <button onClick={onEdit} className="btn btn-ghost">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
                Edit units &amp; fault
              </button>
            )}
            {canDispatch && (
              <button
                onClick={() => { setDispatchOpen(o => !o); setChaseOpen(false); }}
                className={dispatchOpen ? 'btn btn-ghost' : 'btn btn-primary'}
              >{dispatchOpen ? 'Cancel dispatch' : 'Dispatch'}</button>
            )}
            {canReceive && (
              <button onClick={onReceive} className="btn btn-primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                Receive this RMA
              </button>
            )}
            {pendingBench.length > 0 && !isReadOnly && (
              <button onClick={() => onBenchTest(shipment.id)} className="btn btn-primary">
                Bench test {pendingBench.length} unit{pendingBench.length === 1 ? '' : 's'}
              </button>
            )}
            {canClose && (
              <button
                onClick={() => {
                  // Terminal: closing ends the RMA's life and there is no reopen in the UI.
                  // Summarise the outcomes being sealed rather than asking a bare
                  // "are you sure?", so the operator can spot a wrong outcome first.
                  const lines = [
                    'Close ' + (shipment.ref || 'this RMA') + '?',
                    '',
                    devs.length + ' unit' + (devs.length === 1 ? '' : 's') + ' on this RMA:',
                    ...closeSummary.map(c => '• ' + c.n + ' ' + c.label.toLowerCase()),
                    '',
                    'This marks the RMA finished and removes it from the open queue.',
                  ];
                  if (!window.confirm(lines.join('\n'))) return;
                  onClose(shipment.id);
                }}
                className="btn btn-ghost"
              >Close RMA</button>
            )}
          </div>
        </div>

        {dispatchOpen && (() => {
          const dFieldStyle = {
            height: 36, padding: '0 12px', borderRadius: 'var(--radius-lg)', width: '100%',
            background: 'var(--surface-default)', border: '1px solid var(--border-default)',
            color: 'var(--text-default)', fontSize: 13, outline: 'none',
          };
          const sla = slaDaysOf(shipment, settings);
          // Dispatching dates the SLA clock from the date entered, so the expected-back
          // date previewed here is the one the queue will actually chase against.
          const from = dDate ? new Date(dDate) : new Date(now);
          const back = !isNaN(from.getTime())
            ? new Date(from.getTime() + sla * 86400000).toLocaleDateString('en-GB') : '—';
          const noRef = !dTracking.trim();
          return (
            <div style={{
              marginTop: 12, padding: '16px 18px', background: 'var(--surface-default)',
              border: '1px solid var(--brand-border)', borderRadius: 'var(--radius-xl)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                Dispatch this RMA
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginBottom: 14 }}>
                Record how the parcel is going. {devs.length} unit{devs.length === 1 ? '' : 's'} will move to
                “With manufacturer” and leave the bench.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Carrier</div>
                  {FilterSelect
                    ? <FilterSelect
                        value={dCarrier} onChange={setDCarrier} darkMode={darkMode} OW={OW} DM={DM}
                        options={[{ value: '', label: 'Not set' },
                                  ...(settings?.carriers || []).map(c => ({ value: c, label: c }))]}
                      />
                    : <input value={dCarrier} onChange={e => setDCarrier(e.target.value)} style={dFieldStyle} />}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Tracking / reference number
                  </div>
                  <input
                    value={dTracking}
                    onChange={e => setDTracking(e.target.value)}
                    placeholder="e.g. 1Z999AA10123456784"
                    autoFocus
                    style={{ ...dFieldStyle, fontFamily: 'var(--font-mono)' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Dispatch date</div>
                  {DatePicker
                    ? <DatePicker value={dDate} onChange={e => setDDate(e.target.value)} darkMode={darkMode} OW={OW} DM={DM} />
                    : <input type="date" value={dDate} onChange={e => setDDate(e.target.value)} style={dFieldStyle} />}
                </div>
              </div>

              {/* Tracking stays optional — the New RMA screen promises it can be added
                  later, and a courier collection often hands the number over after the van
                  has gone. Warn, but do not block. */}
              {noRef && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 14, padding: '10px 12px',
                  background: 'var(--warning-fill)', border: '1px solid var(--warning-border)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--warning-text)" strokeWidth="2.2"
                       strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
                  </svg>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--warning-text)' }}>
                    No tracking number — this parcel will not be traceable and the claim has no
                    courier reference to evidence it. You can still dispatch and add the number later.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                  SLA {sla} d · expected back {back}
                  {dCarrier && ' · via ' + dCarrier}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => {
                      if (dispatching) return;
                      setDispatching(true);
                      onDispatch({
                        carrier: dCarrier,
                        trackingNumber: dTracking.trim(),
                        // Preserve the time of day when the operator accepts today's date,
                        // so two RMAs dispatched an hour apart do not collide at midnight.
                        dispatchedAt: dDate ? new Date(dDate).toISOString() : undefined,
                      });
                      setDispatchOpen(false);
                      setDispatching(false);
                    }}
                    disabled={dispatching}
                    className="btn btn-primary"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                    Confirm dispatch
                  </button>
                  <button onClick={() => setDispatchOpen(false)} className="btn btn-ghost">Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {chaseOpen && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-start' }}>
            <input
              value={chaseNote}
              onChange={e => setChaseNote(e.target.value)}
              placeholder="What did you chase, and how? (logged against this RMA)"
              style={{
                flex: 1, height: 36, padding: '0 12px', borderRadius: 'var(--radius-lg)',
                background: 'var(--surface-default)', border: '1px solid var(--border-default)',
                color: 'var(--text-default)', fontSize: 13, outline: 'none',
              }}
            />
            <button
              onClick={() => { onChase(shipment.id, chaseNote); setChaseNote(''); setChaseOpen(false); }}
              className="btn btn-primary"
            >Log chase</button>
            <button onClick={() => { setChaseOpen(false); setChaseNote(''); }} className="btn btn-ghost">Cancel</button>
          </div>
        )}
      </div>

      <RmaStageTimeline shipment={shipment} now={now} />

      {/* The right column grows a little on wide screens rather than staying pinned at
          328px, so the device table does not end up absurdly wide next to a thin rail. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(328px,22%)', gap: 16, alignItems: 'start' }}>

        {/* Devices */}
        <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-default)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              Devices on this RMA ({devs.length})
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>
              {Object.keys(outcomes).length === 0
                ? 'no outcomes recorded yet'
                : inWarrantyCount === devs.length
                  ? 'all ' + devs.length + ' within warranty at dispatch'
                  : inWarrantyCount + ' of ' + devs.length + ' within warranty at dispatch'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: devCols, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-inset)' }}>
            <Th>Serial</Th><Th>Type</Th><Th>Reported fault</Th><Th>Warranty</Th><Th>Outcome</Th>
            {canRemove && <Th />}
          </div>
          {devs.length === 0 && <EmptyState title="No devices on this RMA" />}
          {devs.map(d => {
            const w = warrantyShort(d.purchaseDate, months, now);
            const wTone = TONE[w.tone] || TONE.neutral;
            const end = warrantyEnd(d.purchaseDate, months);
            const o = outcomes[d.id];
            const def = o && RMA_OUTCOME[o.outcome];
            const oTone = def ? (TONE[def.tone] || TONE.neutral) : TONE.neutral;
            const fault = shipment.faultMode === 'per_device'
              ? (shipment.faultCategories?.[d.id] || '—')
              : (shipment.faultCategory || '—');
            return (
              <div key={d.id} style={{ display: 'grid', gridTemplateColumns: devCols, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'start' }}>
                <Mono>{d.serial}</Mono>
                <div><DeviceTypeChip type={d.type} /></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-default)' }}>{fault}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 3, lineHeight: 1.45 }}>
                    {shipment.faultDescriptions?.[d.id] || '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: wTone.text }}>{w.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 2 }}>
                    {end ? 'exp ' + end.toLocaleDateString('en-GB') : 'no purchase date'}
                  </div>
                </div>
                <div>
                  {def ? (
                    <>
                      <span className={'pill ' + oTone.pill}>{def.label}</span>
                      <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 4 }}>
                        {def.needsNewSerial && o.newSerial ? 'new serial ' + o.newSerial : def.destLabel}
                        {def.needsBenchTest && (o.benchTested ? ' · bench tested' : ' · awaiting bench test')}
                      </div>
                    </>
                  ) : (
                    <span className="pill pill-neutral">Awaiting assessment</span>
                  )}
                </div>
                {canRemove && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        // Sends the unit back to Quarantine, where it came from. Named in
                        // the prompt because a serial is easy to misclick in a 20-row table.
                        if (!window.confirm(
                          'Remove ' + d.serial + ' from ' + (shipment.ref || 'this RMA') + '?'
                          + '\n\nThe unit returns to Quarantine and can be added to another RMA. '
                          + 'The other ' + (devs.length - 1) + ' unit'
                          + (devs.length - 1 === 1 ? '' : 's') + ' stay on this one.'
                        )) return;
                        onRemoveDevice(shipment.id, d.id);
                      }}
                      className="btn btn-sm btn-danger"
                      aria-label={'Remove ' + d.serial + ' from this RMA'}
                    >Remove</button>
                  </div>
                )}
              </div>
            );
          })}
          {missing > 0 && (
            <div style={{ padding: '11px 18px', fontSize: 11.5, color: 'var(--warning-text)', background: 'var(--warning-fill)' }}>
              {missing} device record{missing === 1 ? '' : 's'} on this RMA no longer exist{missing === 1 ? 's' : ''} in stock.
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', padding: '16px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Shipment</div>
            <FactRow label="Manufacturer" value={shipment.manufacturer || '—'} />
            <FactRow label="Carrier" value={shipment.carrier || 'not set'} valueColor={shipment.carrier ? undefined : 'var(--text-ghost)'} />
            <FactRow label="Tracking" value={shipment.trackingNumber || 'not set'} mono={!!shipment.trackingNumber} valueColor={shipment.trackingNumber ? undefined : 'var(--text-ghost)'} />
            <FactRow label="Dispatched" value={shipment.dispatchedAt ? new Date(shipment.dispatchedAt).toLocaleDateString('en-GB') : 'not yet'} />
            <FactRow label="Dispatched by" value={shipment.dispatchedBy || '—'} />
            <FactRow label="Days with manufacturer" value={awayDays === null ? '—' : String(awayDays)} mono={awayDays !== null} />
            <FactRow label="Expected back" value={shipment.expectedBackAt ? new Date(shipment.expectedBackAt).toLocaleDateString('en-GB') : '—'} last />

            {late && (
              <Callout tone="warning" title={daysOverSla(shipment, now, settings) + ' days over SLA'} style={{ marginTop: 14 }}>
                {(shipment.manufacturer || 'The manufacturer') + "'s agreed turnaround is " + sla + ' days.'}
                {shipment.lastChasedAt
                  ? ' Chase logged ' + Math.max(0, Math.floor((now - new Date(shipment.lastChasedAt)) / 86400000)) + ' days ago.'
                  : ' No chase logged yet.'}
              </Callout>
            )}

            {chargeable.length > 0 && (
              <Callout tone="danger" title={chargeable.length + ' unit' + (chargeable.length === 1 ? '' : 's') + ' chargeable'} style={{ marginTop: 10 }}>
                {chargeable.length === 1
                  ? (() => {
                      const d = chargeable[0];
                      const end = warrantyEnd(d.purchaseDate, months);
                      return d.serial + ' was out of warranty at dispatch'
                        + (end ? ' (expired ' + end.toLocaleDateString('en-GB') + ')' : '')
                        + '. Expect a repair charge for that unit.';
                    })()
                  : chargeable.length + ' units were out of warranty at dispatch. Expect repair charges for those units.'}
              </Callout>
            )}
          </div>

          <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', padding: '16px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Activity</div>
            {(shipment.activity || []).length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-ghost)' }}>Nothing recorded yet.</div>
            )}
            {(shipment.activity || []).map((a, i, arr) => (
              <div key={i} style={{ display: 'flex', gap: 11, padding: '9px 0', borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--border-subtle)' }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                  background: ACTIVITY_DOT[a.kind] || 'var(--text-ghost)',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-default)', fontWeight: 500, lineHeight: 1.4 }}>{a.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 2 }}>
                    {a.at ? new Date(a.at).toLocaleDateString('en-GB') : ''}{a.by ? ' · ' + a.by : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RmaDetail;
